import React, { useCallback, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { api } from '../../../services/api';
import { linkifyFormPublicUrls } from '../../../utils/formPublicLink';

interface EmailMessageBubbleProps {
  content?: string | null;
  contentAttributes?: string | null;
  conversationId?: string;
  messageId?: string;
  formLinkTitles?: ReadonlyMap<string, string>;
  className?: string;
}

interface EmailAttributes {
  htmlContent?: {
    full?: string | null;
  };
  textContent?: {
    full?: string | null;
  };
  signatureImageObjectKey?: string | null;
  inlineImages?: Array<{
    contentId?: string | null;
  }>;
}

const parseEmailAttributes = (contentAttributes?: string | null): EmailAttributes | null => {
  if (!contentAttributes)
  {
    return null;
  }

  try
  {
    const parsed = JSON.parse(contentAttributes) as { email?: EmailAttributes };
    return parsed.email ?? null;
  }
  catch
  {
    return null;
  }
};

const escapeHtml = (value: string): string => value
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

const isHtml = (value: string): boolean => /<\s*[a-z][^>]*>/i.test(value);

const EMPTY_INLINE_IMAGES: NonNullable<EmailAttributes['inlineImages']> = [];
const EMPTY_FORM_TITLES: ReadonlyMap<string, string> = new Map();

const isEmptyImageUrlMap = (urls: Record<string, string>): boolean =>
  Object.keys(urls).length === 0;

const sanitizeEmailHtml = (content: string, showQuoted: boolean, formLinkTitles: ReadonlyMap<string, string>): string => {
  if (!isHtml(content))
  {
    const escaped = escapeHtml(content)
      .replaceAll('\r\n', '<br />')
      .replaceAll('\n', '<br />')
      .replaceAll('\r', '<br />');
    return linkifyFormPublicUrls(`<div class="email-plain-text">${escaped}</div>`, formLinkTitles);
  }

  const parsed = new DOMParser().parseFromString(content, 'text/html');
  if (!showQuoted)
  {
    parsed.querySelectorAll('blockquote, #gmail_quote, .gmail_quote').forEach((node) => node.remove());
  }

  const sanitized = DOMPurify.sanitize(parsed.body.innerHTML, {
    FORBID_TAGS: ['applet', 'base', 'embed', 'form', 'iframe', 'link', 'meta', 'object', 'script', 'style'],
    FORBID_ATTR: ['srcdoc'],
  });
  return linkifyFormPublicUrls(sanitized, formLinkTitles);
};

const buildDocument = (content: string, showQuoted: boolean, formLinkTitles: ReadonlyMap<string, string>): string => {
  const body = sanitizeEmailHtml(content, showQuoted, formLinkTitles);

  return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<base target="_blank">
<style>
  html, body { margin: 0; padding: 0; background: transparent; }
  body { color: #1f2937; font: 14px/1.5 Arial, sans-serif; overflow-wrap: anywhere; }
  img { max-width: 100%; height: auto; }
  table { max-width: 100%; }
  a { color: #2563eb; }
  .email-plain-text { overflow-wrap: anywhere; }
  .email-signature-loading { display: inline-block; margin-top: 8px; color: #6b7280; font-size: 12px; }
  .email-signature-image { max-width: 100%; height: auto; }
</style>
</head>
<body>${body}</body>
</html>`;
};

const openEmailLinkInNewTab = (event: MouseEvent): void => {
  if (event.defaultPrevented || event.button !== 0)
  {
    return;
  }

  const target = event.target;
  if (!(target instanceof Element))
  {
    return;
  }

  const anchor = target.closest('a[href]');
  if (!(anchor instanceof HTMLAnchorElement))
  {
    return;
  }

  const rawHref = anchor.getAttribute('href')?.trim() ?? '';
  if (!rawHref || rawHref.startsWith('#'))
  {
    return;
  }

  let protocol = '';
  try
  {
    protocol = new URL(anchor.href).protocol;
  }
  catch
  {
    return;
  }

  if (protocol !== 'http:' && protocol !== 'https:' && protocol !== 'mailto:')
  {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  window.open(anchor.href, '_blank', 'noopener,noreferrer');
};

const getDocumentHeight = (frame: HTMLIFrameElement): number => {
  const document = frame.contentDocument;
  if (!document)
  {
    return 96;
  }

  const bodyHeight = document.body?.scrollHeight ?? 0;
  const documentHeight = document.documentElement?.scrollHeight ?? 0;
  return Math.min(Math.max(bodyHeight, documentHeight, 48), 600);
};

export const EmailMessageBubble: React.FC<EmailMessageBubbleProps> = ({
  content,
  contentAttributes,
  conversationId,
  messageId,
  formLinkTitles = EMPTY_FORM_TITLES,
  className = '',
}) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(96);
  const [showQuoted, setShowQuoted] = useState(false);
  const [signatureImageUrl, setSignatureImageUrl] = useState<string | null>(null);
  const [signatureImageLoadError, setSignatureImageLoadError] = useState(false);
  const [signatureImageLoaded, setSignatureImageLoaded] = useState(false);
  const [inlineImageUrls, setInlineImageUrls] = useState<Record<string, string>>({});
  
  const emailAttributes = useMemo(
    () => parseEmailAttributes(contentAttributes),
    [contentAttributes],
  );
  
  const html = emailAttributes?.htmlContent?.full?.trim() || content?.trim() || '';
  const text = emailAttributes?.textContent?.full?.trim() || content?.trim() || '';
  const hasQuotedContent = /<\s*blockquote\b|gmail_quote/i.test(html);
  const signatureImageKey = emailAttributes?.signatureImageObjectKey?.trim();

  React.useEffect(() => {
    let cancelled = false;

    setSignatureImageUrl(null);
    setSignatureImageLoadError(false);
    setSignatureImageLoaded(false);
    if (!signatureImageKey)
    {
      return () => {
        cancelled = true;
      };
    }

    api.get<{ url: string }>('/support/user-signature/image-url', {
      params: { objectKey: signatureImageKey },
    })
      .then((response) => {
        if (!cancelled)
        {
          setSignatureImageUrl(response.data.url);
          setSignatureImageLoadError(false);
          setSignatureImageLoaded(false);
        }
      })
      .catch(() => {
        if (!cancelled)
        {
          setSignatureImageUrl(null);
          setSignatureImageLoadError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [signatureImageKey]);

  const inlineImages = emailAttributes?.inlineImages ?? EMPTY_INLINE_IMAGES;

  React.useEffect(() => {
    let cancelled = false;

    if (!conversationId || !messageId || inlineImages.length === 0)
    {
      setInlineImageUrls((current) => (isEmptyImageUrlMap(current) ? current : {}));
      return () => {
        cancelled = true;
      };
    }

    setInlineImageUrls((current) => (isEmptyImageUrlMap(current) ? current : {}));

    const loadInlineImages = async () => {
      const resolved = await Promise.all(
        inlineImages.map(async (image) => {
          const contentId = image.contentId?.trim();
          if (!contentId)
          {
            return null;
          }

          try
          {
            const response = await api.get<{ url: string }>(
              `/conversations/${conversationId}/messages/${messageId}/inline-image`,
              { params: { contentId } },
            );
            return { contentId, url: response.data.url };
          }
          catch
          {
            return null;
          }
        }),
      );

      if (cancelled)
      {
        return;
      }

      setInlineImageUrls(
        Object.fromEntries(
          resolved
            .filter((item): item is { contentId: string; url: string } => item !== null)
            .map((item) => [item.contentId.toLowerCase(), item.url]),
        ),
      );
    };

    void loadInlineImages();
    return () => {
      cancelled = true;
    };
  }, [conversationId, messageId, inlineImages]);

  const htmlWithImage = useMemo(() => {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    parsed.querySelectorAll('img[data-know-email-cid]').forEach((image) => {
      const contentId = image.getAttribute('data-know-email-cid')?.toLowerCase();
      const url = contentId ? inlineImageUrls[contentId] : undefined;
      if (!url)
      {
        image.remove();
        return;
      }

      image.setAttribute('src', url);
      image.removeAttribute('data-know-email-cid');
    });

    const withInlineImages = parsed.body.innerHTML;
    const withoutCidImage = withInlineImages.replace(
      /<img\b[^>]*\bsrc\s*=\s*["']cid:know-user-signature["'][^>]*>/gi,
      '',
    );

    if (!signatureImageUrl)
    {
      const shouldShowSignatureImageLoading =
        Boolean(signatureImageKey) && !signatureImageLoadError;
      const signaturePlaceholder = shouldShowSignatureImageLoading
        ? '<span data-know-signature-loading="1" class="email-signature-loading">Carregando imagem da assinatura…</span>'
        : '';
      const withoutPlaceholder = withoutCidImage.replace(
        /<img\b[^>]*\bdata-know-signature\s*=\s*["']1["'][^>]*>/gi,
        signaturePlaceholder,
      );
      return withoutPlaceholder || text;
    }

    const escapedUrl = escapeHtml(signatureImageUrl);
    const signatureLoadingPlaceholder =
      '<span data-know-signature-loading="1" class="email-signature-loading">Carregando imagem da assinatura…</span>';
    const withResolvedSrc = withoutCidImage.replace(
      /<img\b([^>]*\bdata-know-signature\s*=\s*["']1["'][^>]*)>/gi,
      `${signatureImageLoaded ? '' : signatureLoadingPlaceholder}<img src="${escapedUrl}"$1>`,
    );

    if (withResolvedSrc !== withoutCidImage)
    {
      return withResolvedSrc;
    }

    const image = `${signatureImageLoaded ? '' : signatureLoadingPlaceholder}<br /><img data-know-signature="1" class="email-signature-image" src="${escapedUrl}" alt="Assinatura" />`;
    const closingDivIndex = withResolvedSrc.lastIndexOf('</div>');
    if (closingDivIndex === -1)
    {
      return `${withResolvedSrc}${image}`;
    }

    return `${withResolvedSrc.slice(0, closingDivIndex)}${image}${withResolvedSrc.slice(closingDivIndex)}`;
  }, [
    html,
    text,
    signatureImageKey,
    signatureImageUrl,
    signatureImageLoadError,
    signatureImageLoaded,
    inlineImageUrls,
  ]);

  const srcDoc = useMemo(
    () => buildDocument(htmlWithImage, showQuoted, formLinkTitles),
    [htmlWithImage, showQuoted, formLinkTitles],
  );

  const handleLoad = useCallback(() => {
    const frame = frameRef.current;
    if (!frame)
    {
      return;
    }

    const signatureImage = frame.contentDocument?.querySelector(
      'img[data-know-signature="1"]',
    ) as HTMLImageElement | null;
    if (!signatureImage)
    {
      setSignatureImageLoaded(true);
    }
    else if (signatureImage.complete)
    {
      setSignatureImageLoaded(true);
    }
    else
    {
      const markSignatureImageAsLoaded = () => {
        setSignatureImageLoaded(true);
      };
      signatureImage.addEventListener('load', markSignatureImageAsLoaded, { once: true });
      signatureImage.addEventListener('error', markSignatureImageAsLoaded, { once: true });
    }

    frame.contentDocument?.addEventListener('click', openEmailLinkInNewTab, true);
    setHeight(getDocumentHeight(frame));
  }, []);

  if (!html && !text)
  {
    return <em className="opacity-70">(sem conteúdo)</em>;
  }

  return (
    <div className={`w-full overflow-hidden rounded-lg bg-white/70 ${className}`}>
      {hasQuotedContent && (
        <button
          type="button"
          onClick={() => setShowQuoted((visible) => !visible)}
          className="mb-1 px-2 text-xs text-blue-600 hover:underline"
        >
          {showQuoted ? 'Ocultar mensagem citada' : 'Mostrar mensagem citada'}
        </button>
      )}
      <iframe
        ref={frameRef}
        title="Conteúdo do e-mail"
        srcDoc={srcDoc}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        onLoad={handleLoad}
        style={{ height: `${height}px`, maxHeight: '600px', minHeight: '48px' }}
        className="block w-full border-0"
      />
    </div>
  );
};
